import React from 'react';
import {
    AudioInputDevice,
    TherapySession,
    TherapySessionParticipant,
    TherapySessionTranscriptUtterance,
    UserType,
} from '../interfaces';
import { Button } from './Button';
import { getFirstFirestoreTimestampDate } from '../firestoreTimestamp';
import { SessionAudioPlayer } from './SessionAudioPlayer';
import { MicrophoneLevelMeter, MicrophoneLevelMeterHandle } from './MicrophoneLevelMeter';
import { hasExplicitSessionParticipantAssociation } from '../sessionParticipants';
import './SessionRecorderControl.css';

type SessionRecordingStatus = 'idle' | 'recording' | 'saving';

type Props = {
    healthcareProviderId: string;
    canRecord: boolean;
    status: SessionRecordingStatus;
    participantOptions: Array<TherapySessionParticipant>;
    defaultParticipant?: TherapySessionParticipant;
    defaultClientParticipant?: TherapySessionParticipant;
    audioInputDevices: Array<AudioInputDevice>;
    selectedAudioInputDeviceId: string;
    onAudioInputDeviceChange: (deviceId: string) => void;
    onRefreshAudioInputDevices: () => Promise<void>;
    onRecord: (participants: Array<TherapySessionParticipant>) => Promise<boolean>;
    unassignedSessions?: Array<TherapySession>;
    onDeleteUnassignedSession?: (session: TherapySession) => Promise<void>;
    onEnsureSessionWaveform?: (sessionId: string) => Promise<void>;
};

const MAX_VISIBLE_SUGGESTIONS = 8;

// Keep participant identity stable when two people happen to share a display name.
function getParticipantKey(participant: TherapySessionParticipant): string {
    return `${participant.personType}:${participant.personId}`;
}

// Use the dashboard's familiar people labels without exposing contact details.
function getParticipantTypeLabel(participant: TherapySessionParticipant): string {
    if (participant.personType === 'therapist') {
        return 'Team member';
    }

    const labels: Partial<Record<UserType, string>> = {
        care_recipient: 'Care recipient',
        carer: 'Carer',
        therapist: 'Therapist client record',
        admin: 'Admin client record',
        super: 'Super client record',
        root: 'Root client record',
    };
    return labels[participant.userType || 'care_recipient'] || 'Client';
}

// Prefer normalized transcript metadata while supporting older top-level fields.
function getSessionUtterances(session: TherapySession): Array<TherapySessionTranscriptUtterance> {
    return session.metadata?.utterances?.length ?
        session.metadata.utterances : session.transcriptUtterances || [];
}

// Keep the provider-level history compact while still identifying group participants.
function getUnassignedSessionTitle(session: TherapySession): string {
    if (session.title?.trim()) {
        return session.title.trim();
    }
    const participantNames = Array.from(new Set(
        (session.participants || []).map((participant) => participant.displayName).filter(Boolean)
    ));
    return participantNames.length > 0 ?
        `Session with ${participantNames.join(', ')}` : 'Session without named participants';
}

// Format stored session timestamps for the lightweight global history.
function getUnassignedSessionDateLabel(session: TherapySession): string {
    const sessionDate = getFirstFirestoreTimestampDate(session.date, session.startedAt, session.createdAt);
    return sessionDate ? sessionDate.toLocaleString() : 'Date unavailable';
}

// Show named assignments when AssemblyAI identified a selected participant.
function getUtteranceSpeakerLabel(utterance: TherapySessionTranscriptUtterance): string {
    if (utterance.speakerAssignment?.displayName) {
        return utterance.speakerAssignment.displayName;
    }
    if ((utterance.role || '').toLowerCase().includes('therapist')) return 'Therapist';
    if ((utterance.role || '').toLowerCase().includes('client')) return 'Client';
    return utterance.role && utterance.role !== 'unknown' ? utterance.role : `Speaker ${utterance.channel}`;
}

// Render the global recorder launcher and its participant/microphone form.
export function SessionRecorderControl(props: Props) {
    const {
        healthcareProviderId,
        canRecord,
        status,
        participantOptions,
        defaultParticipant,
        defaultClientParticipant,
        audioInputDevices,
        selectedAudioInputDeviceId,
        onAudioInputDeviceChange,
        onRefreshAudioInputDevices,
        onRecord,
        unassignedSessions = [],
        onDeleteUnassignedSession,
        onEnsureSessionWaveform,
    } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedParticipants, setSelectedParticipants] = React.useState<Array<TherapySessionParticipant>>([]);
    const [query, setQuery] = React.useState('');
    const [isParticipantPickerExpanded, setIsParticipantPickerExpanded] = React.useState(false);
    const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = React.useState(0);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [microphonePreviewVersion, setMicrophonePreviewVersion] = React.useState(0);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [announcement, setAnnouncement] = React.useState('');
    const [expandedHistorySessionId, setExpandedHistorySessionId] = React.useState('');
    const [deletingHistorySessionIds, setDeletingHistorySessionIds] = React.useState<Record<string, boolean>>({});
    const [historyErrorMessage, setHistoryErrorMessage] = React.useState('');
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const dialogRef = React.useRef<HTMLFormElement | null>(null);
    const participantInputRef = React.useRef<HTMLInputElement | null>(null);
    const microphoneLevelRef = React.useRef<MicrophoneLevelMeterHandle | null>(null);
    const defaultParticipantsRef = React.useRef([defaultParticipant, defaultClientParticipant]);
    const titleId = React.useId();
    const participantHelpId = React.useId();
    const participantListId = React.useId();

    React.useEffect(() => {
        defaultParticipantsRef.current = [defaultParticipant, defaultClientParticipant];
    }, [defaultParticipant, defaultClientParticipant]);

    const resetDraft = React.useCallback(() => {
        // Snapshot defaults on opening so refreshing data never restores a manually removed chip.
        const defaults = defaultParticipantsRef.current.filter(
            (participant): participant is TherapySessionParticipant => !!participant
        );
        setSelectedParticipants(Array.from(new Map(defaults.map((participant) =>
            [getParticipantKey(participant), participant]
        )).values()));
        setQuery('');
        setIsParticipantPickerExpanded(false);
        setHighlightedSuggestionIndex(0);
        setErrorMessage('');
        setAnnouncement('');
    }, []);

    const closeDialog = React.useCallback((restoreFocus = true) => {
        if (isSubmitting) {
            return;
        }

        setIsOpen(false);
        setIsParticipantPickerExpanded(false);
        setQuery('');
        setErrorMessage('');
        if (restoreFocus) {
            triggerRef.current?.focus();
        }
    }, [isSubmitting]);

    const openDialog = () => {
        if (!canRecord || status !== 'idle') {
            return;
        }

        resetDraft();
        setIsOpen(true);
    };

    React.useEffect(() => {
        // Provider changes invalidate every person selected from the previous directory.
        setIsOpen(false);
        setIsParticipantPickerExpanded(false);
        resetDraft();
    }, [healthcareProviderId, resetDraft]);

    React.useEffect(() => {
        // Starting or saving replaces the setup dialog with the persistent recorder strip.
        if (status !== 'idle' && isOpen) {
            setIsOpen(false);
            setIsParticipantPickerExpanded(false);
        }
    }, [isOpen, status]);

    React.useEffect(() => {
        // Revoked feature access must not leave an invisible modal or focus trap behind.
        if (!canRecord) {
            setIsOpen(false);
            setIsParticipantPickerExpanded(false);
            setQuery('');
            setErrorMessage('');
        }
    }, [canRecord]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Focus the participant combobox and keep keyboard focus inside the modal.
        participantInputRef.current?.focus();
        const handleDocumentKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (isParticipantPickerExpanded && document.activeElement === participantInputRef.current) {
                    setIsParticipantPickerExpanded(false);
                    return;
                }
                closeDialog();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) {
                return;
            }

            const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
                'button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), ' +
                'select:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ));
            if (focusableElements.length === 0) {
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => document.removeEventListener('keydown', handleDocumentKeyDown);
    }, [closeDialog, isOpen, isParticipantPickerExpanded]);

    const selectedKeys = React.useMemo(
        () => new Set(selectedParticipants.map(getParticipantKey)),
        [selectedParticipants]
    );
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const suggestions = React.useMemo(() => participantOptions
        .filter((participant) => !selectedKeys.has(getParticipantKey(participant)))
        .filter((participant) => !normalizedQuery || [
            participant.displayName,
            getParticipantTypeLabel(participant),
        ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery)))
        .slice(0, MAX_VISIBLE_SUGGESTIONS), [normalizedQuery, participantOptions, selectedKeys]);
    const isParticipantPickerOpen = isParticipantPickerExpanded;
    const recordingsWithoutParticipants = React.useMemo(
        () => unassignedSessions.filter((session) => !hasExplicitSessionParticipantAssociation(session)),
        [unassignedSessions]
    );

    React.useEffect(() => {
        // A changing filter must never leave the keyboard highlight out of range.
        setHighlightedSuggestionIndex(0);
    }, [normalizedQuery, participantOptions.length, selectedParticipants.length]);

    const addParticipant = (participant: TherapySessionParticipant) => {
        if (selectedKeys.has(getParticipantKey(participant))) {
            return;
        }

        setSelectedParticipants((currentParticipants) => [...currentParticipants, participant]);
        setQuery('');
        setAnnouncement(`${participant.displayName} added.`);
        participantInputRef.current?.focus();
    };

    const removeParticipant = (participant: TherapySessionParticipant) => {
        setSelectedParticipants((currentParticipants) => currentParticipants.filter(
            (candidate) => getParticipantKey(candidate) !== getParticipantKey(participant)
        ));
        setAnnouncement(`${participant.displayName} removed.`);
    };

    const handleParticipantInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' && suggestions.length > 0) {
            event.preventDefault();
            if (!isParticipantPickerOpen) {
                setIsParticipantPickerExpanded(true);
                setHighlightedSuggestionIndex(0);
            } else {
                setHighlightedSuggestionIndex((currentIndex) => (currentIndex + 1) % suggestions.length);
            }
            return;
        }

        if (event.key === 'ArrowUp' && isParticipantPickerOpen && suggestions.length > 0) {
            event.preventDefault();
            setHighlightedSuggestionIndex((currentIndex) =>
                currentIndex === 0 ? suggestions.length - 1 : currentIndex - 1
            );
            return;
        }

        if ((event.key === 'Enter' || event.key === ',') &&
            isParticipantPickerOpen && suggestions.length > 0) {
            event.preventDefault();
            addParticipant(suggestions[highlightedSuggestionIndex] || suggestions[0]);
            return;
        }

        if (event.key === 'Enter' && query.trim()) {
            // Unmatched text is not a participant and should not accidentally start recording.
            event.preventDefault();
            return;
        }

        if (event.key === 'Backspace' && !query && selectedParticipants.length > 0) {
            event.preventDefault();
            removeParticipant(selectedParticipants[selectedParticipants.length - 1]);
        }
    };

    const handleRecord = async () => {
        // Guard form submission as well as the button, including keyboard submission.
        if (isSubmitting || status !== 'idle' || selectedParticipants.length === 0) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');
        // Release the preview before the recorder requests exclusive access to this microphone.
        microphoneLevelRef.current?.stop();
        try {
            const didStart = await onRecord(selectedParticipants);
            if (didStart) {
                setIsOpen(false);
                setIsParticipantPickerExpanded(false);
                setQuery('');
            } else {
                setErrorMessage('Recording could not be started. Check microphone access and try again.');
            }
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Recording could not be started.');
        } finally {
            // Restart even when recording fails before React commits the submitting state.
            setMicrophonePreviewVersion((version) => version + 1);
            setIsSubmitting(false);
        }
    };

    const handleDeleteUnassignedSession = async (session: TherapySession) => {
        if (!onDeleteUnassignedSession || deletingHistorySessionIds[session.id]) {
            return;
        }
        const confirmed = window.confirm(`Delete "${getUnassignedSessionTitle(session)}" and its audio recording?`);
        if (!confirmed) {
            return;
        }

        setDeletingHistorySessionIds((currentIds) => ({...currentIds, [session.id]: true}));
        setHistoryErrorMessage('');
        try {
            await onDeleteUnassignedSession(session);
            setExpandedHistorySessionId((currentId) => currentId === session.id ? '' : currentId);
        } catch (error) {
            setHistoryErrorMessage(error instanceof Error ? error.message : 'The recording could not be deleted.');
        } finally {
            setDeletingHistorySessionIds((currentIds) => {
                const nextIds = {...currentIds};
                delete nextIds[session.id];
                return nextIds;
            });
        }
    };

    if (!canRecord) {
        return null;
    }

    const isLauncherDisabled = status !== 'idle';

    return (
        <>
            <button
                ref={triggerRef}
                type='button'
                className='session-recorder-launcher'
                onClick={openDialog}
                disabled={isLauncherDisabled}
                aria-label={isLauncherDisabled ? 'Session recording in progress' : 'Record a session'}
                aria-haspopup='dialog'
                aria-expanded={isOpen}
                aria-controls={isOpen ? `${titleId}-dialog` : undefined}
                title={isLauncherDisabled ? 'Session recording in progress' : 'Record a session'}
            >
                <span className='session-recorder-launcher-mark' aria-hidden='true'>
                    <span />
                </span>
                <span className='session-recorder-launcher-label' aria-hidden='true'>REC</span>
            </button>

            {isOpen && (
                <div
                    className='session-recorder-overlay'
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closeDialog();
                        }
                    }}
                >
                    <form
                        id={`${titleId}-dialog`}
                        ref={dialogRef}
                        className='session-recorder-dialog'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby={titleId}
                        onSubmit={(event) => {
                            event.preventDefault();
                            void handleRecord();
                        }}
                    >
                        <div className='session-recorder-dialog-header'>
                            <div>
                                <h2 id={titleId}>Record a session</h2>
                                <p>Choose who is present, or record without a client participant.</p>
                            </div>
                            <button
                                type='button'
                                className='session-recorder-close-button'
                                onClick={() => closeDialog()}
                                aria-label='Close recording setup'
                                disabled={isSubmitting}
                            >
                                <span aria-hidden='true'>&times;</span>
                            </button>
                        </div>

                        <div className='session-recorder-dialog-body'>
                            <div className='session-recorder-field'>
                                <label htmlFor={`${titleId}-microphone`}>Microphone</label>
                                <select
                                    id={`${titleId}-microphone`}
                                    value={selectedAudioInputDeviceId}
                                    onChange={(event) => {
                                        microphoneLevelRef.current?.stop();
                                        onAudioInputDeviceChange(event.target.value);
                                    }}
                                    disabled={isSubmitting}
                                >
                                    <option value=''>Default microphone</option>
                                    {audioInputDevices.map((device) => (
                                        <option key={device.deviceId} value={device.deviceId}>
                                            {device.label}
                                        </option>
                                    ))}
                                </select>
                                <MicrophoneLevelMeter
                                    key={`${healthcareProviderId}:${microphonePreviewVersion}`}
                                    ref={microphoneLevelRef}
                                    deviceId={selectedAudioInputDeviceId}
                                    enabled={canRecord && status === 'idle' && !isSubmitting}
                                    onMicrophoneReady={onRefreshAudioInputDevices}
                                />
                            </div>

                            <div className='session-recorder-field'>
                                <label htmlFor={`${titleId}-participants`}>Participants</label>
                                <p id={participantHelpId} className='session-recorder-help'>
                                    Select at least one Client or Team member. Search and select every person present.
                                </p>
                                <div
                                    className='session-recorder-combobox-shell'
                                    onClick={() => participantInputRef.current?.focus()}
                                >
                                    {selectedParticipants.map((participant) => (
                                        <span className='session-recorder-chip' key={getParticipantKey(participant)}>
                                            <span>{participant.displayName}</span>
                                            <span className='session-recorder-chip-type'>
                                                {getParticipantTypeLabel(participant)}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    removeParticipant(participant);
                                                }}
                                                aria-label={`Remove ${participant.displayName}`}
                                                disabled={isSubmitting}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        ref={participantInputRef}
                                        id={`${titleId}-participants`}
                                        type='text'
                                        role='combobox'
                                        value={query}
                                        onChange={(event) => {
                                            setQuery(event.target.value);
                                            setIsParticipantPickerExpanded(true);
                                        }}
                                        onFocus={() => setIsParticipantPickerExpanded(true)}
                                        onKeyDown={handleParticipantInputKeyDown}
                                        placeholder={selectedParticipants.length === 0 ? 'Type a name…' : 'Add another…'}
                                        aria-autocomplete='list'
                                        aria-expanded={isParticipantPickerOpen}
                                        aria-controls={isParticipantPickerOpen ? participantListId : undefined}
                                        aria-describedby={participantHelpId}
                                        aria-activedescendant={isParticipantPickerOpen && suggestions.length > 0 ?
                                            `${participantListId}-${highlightedSuggestionIndex}` : undefined}
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type='button'
                                        className='session-recorder-picker-toggle'
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setIsParticipantPickerExpanded((isExpanded) => !isExpanded);
                                        }}
                                        aria-label={isParticipantPickerOpen ?
                                            'Collapse participant suggestions' : 'Expand participant suggestions'}
                                        aria-expanded={isParticipantPickerOpen}
                                        aria-controls={isParticipantPickerOpen ? participantListId : undefined}
                                        disabled={isSubmitting}
                                    >
                                        <span
                                            className={isParticipantPickerOpen ?
                                                'session-recorder-picker-chevron is-expanded' :
                                                'session-recorder-picker-chevron'}
                                            aria-hidden='true'
                                        >
                                            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                                                <polyline
                                                    points='3.5 5.5 8 10 12.5 5.5'
                                                    stroke='currentColor'
                                                    strokeWidth='1.7'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                />
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                                {/* The picker stays in document flow so it pushes the footer down instead of covering it. */}
                                {isParticipantPickerOpen && (
                                    <div id={participantListId} className='session-recorder-suggestions' role='listbox'>
                                        {suggestions.length > 0 ? suggestions.map((participant, index) => (
                                            <button
                                                id={`${participantListId}-${index}`}
                                                key={getParticipantKey(participant)}
                                                type='button'
                                                role='option'
                                                tabIndex={-1}
                                                aria-selected={index === highlightedSuggestionIndex}
                                                className={index === highlightedSuggestionIndex ? 'is-highlighted' : ''}
                                                onMouseDown={(event) => event.preventDefault()}
                                                onMouseEnter={() => setHighlightedSuggestionIndex(index)}
                                                onClick={() => addParticipant(participant)}
                                            >
                                                <span>{participant.displayName}</span>
                                                <span>{getParticipantTypeLabel(participant)}</span>
                                            </button>
                                        )) : (
                                            <p className='session-recorder-suggestions-empty'>No matching people.</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className='session-recorder-live-region' aria-live='polite'>
                                {announcement}
                            </div>
                            {!!errorMessage && <p className='session-recorder-error' role='alert'>{errorMessage}</p>}

                            {recordingsWithoutParticipants.length > 0 && (
                                <details className='session-recorder-history'>
                                    <summary>
                                        <span>Recordings without participants</span>
                                        <span>{recordingsWithoutParticipants.length}</span>
                                    </summary>
                                    <p>
                                        Recordings with no selected Client or Team participant stay here.
                                    </p>
                                    <div className='session-recorder-history-list'>
                                        {recordingsWithoutParticipants.map((session) => {
                                            const isExpanded = expandedHistorySessionId === session.id;
                                            const isDeleting = !!deletingHistorySessionIds[session.id];
                                            const utterances = getSessionUtterances(session);
                                            return (
                                                <article className='session-recorder-history-card' key={session.id}>
                                                    <div className='session-recorder-history-card-header'>
                                                        <button
                                                            type='button'
                                                            className='session-recorder-history-toggle'
                                                            onClick={() => setExpandedHistorySessionId(
                                                                isExpanded ? '' : session.id
                                                            )}
                                                            aria-expanded={isExpanded}
                                                            aria-label={`${isExpanded ? 'Collapse' : 'Open'} ${getUnassignedSessionTitle(session)}`}
                                                        >
                                                            <span>{getUnassignedSessionTitle(session)}</span>
                                                            <span>{getUnassignedSessionDateLabel(session)}</span>
                                                        </button>
                                                        {onDeleteUnassignedSession && (
                                                            <button
                                                                type='button'
                                                                className='session-recorder-history-delete'
                                                                onClick={() => void handleDeleteUnassignedSession(session)}
                                                                disabled={isDeleting}
                                                                aria-label={`Delete ${getUnassignedSessionTitle(session)}`}
                                                            >
                                                                {isDeleting ? 'Deleting…' : 'Delete'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {isExpanded && (
                                                        <div className='session-recorder-history-card-body'>
                                                            <SessionAudioPlayer
                                                                session={session}
                                                                utterances={utterances}
                                                                onEnsureWaveform={onEnsureSessionWaveform}
                                                            />
                                                            {utterances.length > 0 ? (
                                                                <div className='session-recorder-history-transcript'>
                                                                    {utterances.map((utterance, index) => (
                                                                        <p key={`${utterance.channel}:${utterance.startMs}:${index}`}>
                                                                            <strong>{getUtteranceSpeakerLabel(utterance)}</strong>
                                                                            <span>{utterance.text}</span>
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p className='session-recorder-history-status'>
                                                                    {session.transcriptStatus === 'error' ?
                                                                        session.transcriptError || 'Transcript could not be processed.' :
                                                                        session.transcriptStatus === 'pending' || session.transcriptStatus === 'processing' ?
                                                                            'Transcript processing…' : 'No transcript is available.'}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </article>
                                            );
                                        })}
                                    </div>
                                    {!!historyErrorMessage && (
                                        <p className='session-recorder-error' role='alert'>{historyErrorMessage}</p>
                                    )}
                                </details>
                            )}

                        </div>

                        <div className='session-recorder-actions'>
                            <Button
                                onClick={() => closeDialog()}
                                label='Cancel'
                                type='secondary'
                                disabled={isSubmitting}
                            />
                            <Button
                                label={isSubmitting ? 'Starting…' : 'Record'}
                                type='primary'
                                htmlType='submit'
                                disabled={isSubmitting || selectedParticipants.length === 0}
                            />
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
